function trim(s) {
  return String(s || '').trim();
}

function parseUrlParts(input) {
  const s = trim(input);
  if (!s) return null;
  const normalized = /^https?:\/\//i.test(s) ? s : `https://${s.replace(/^\/+/, '')}`;
  try {
    const u = new URL(normalized);
    const host = u.hostname.replace(/^www\./i, '').toLowerCase();
    const parts = u.pathname.split('/').filter(Boolean);
    return { host, parts, href: u.href };
  } catch {
    return null;
  }
}

/**
 * Pull a handle/username from a pasted profile URL, or return '' if unknown.
 */
function extractFromUrl(platform, urlString) {
  const parsed = parseUrlParts(urlString);
  if (!parsed) return '';
  const { host, parts } = parsed;

  switch (platform) {
    case 'instagram':
      if (!host.includes('instagram.com')) return '';
      if (!parts[0] || ['p', 'reel', 'reels', 'stories', 'explore', 'tv'].includes(parts[0])) return '';
      return parts[0].replace(/^@/, '');
    case 'twitter':
      if (!host.includes('twitter.com') && host !== 'x.com') return '';
      if (!parts[0] || ['intent', 'share', 'home', 'search', 'i', 'hashtag', 'settings'].includes(parts[0])) {
        return '';
      }
      return parts[0].replace(/^@/, '');
    case 'facebook':
      if (!host.includes('facebook.com') && !host.includes('fb.com')) return '';
      if (parts[0] === 'profile.php') return '';
      if (!parts[0]) return '';
      return parts[0];
    case 'linkedin':
      if (!host.includes('linkedin.com')) return '';
      {
        const i = parts.indexOf('in');
        if (i >= 0 && parts[i + 1]) return parts[i + 1];
        const c = parts.indexOf('company');
        if (c >= 0 && parts[c + 1]) return `company/${parts[c + 1]}`;
      }
      return '';
    case 'youtube':
      if (!host.includes('youtube.com') && host !== 'youtu.be') return '';
      if (parts[0]?.startsWith('@')) return parts[0].slice(1);
      if (parts[0] === 'channel' && parts[1]) return `channel:${parts[1]}`;
      if (parts[0] === 'c' && parts[1]) return parts[1];
      if (parts[0] === 'user' && parts[1]) return parts[1];
      return '';
    default:
      return '';
  }
}

function buildCanonicalUrl(platform, handle) {
  const h = trim(handle);
  if (!h) return '';
  if (/^https?:\/\//i.test(h)) {
    try {
      return new URL(h).href;
    } catch {
      return '';
    }
  }
  switch (platform) {
    case 'instagram':
      return `https://www.instagram.com/${encodeURIComponent(h)}/`;
    case 'twitter':
      return `https://twitter.com/${encodeURIComponent(h)}`;
    case 'facebook':
      return `https://www.facebook.com/${encodeURIComponent(h)}`;
    case 'linkedin':
      if (h.startsWith('company/')) {
        const slug = h.slice('company/'.length);
        return `https://www.linkedin.com/company/${encodeURIComponent(slug)}/`;
      }
      return `https://www.linkedin.com/in/${encodeURIComponent(h)}/`;
    case 'youtube':
      if (h.startsWith('channel:')) {
        const id = h.slice('channel:'.length);
        return `https://www.youtube.com/channel/${encodeURIComponent(id)}`;
      }
      return `https://www.youtube.com/@${encodeURIComponent(h.replace(/^@+/, ''))}`;
    default:
      return '';
  }
}

/**
 * Normalize admin input to a stored handle (username) when possible.
 * Full URLs are reduced to handles; unparseable URLs are kept as-is for legacy rows.
 */
export function normalizeTeamSocialHandle(platform, raw) {
  const s = trim(raw);
  if (!s) return '';
  const looksLikeUrl = /^https?:\/\//i.test(s) || /^[\w.-]+\.[a-z]{2,}\//i.test(s);
  if (looksLikeUrl) {
    const candidate = /^https?:\/\//i.test(s) ? s : `https://${s.replace(/^\/+/, '')}`;
    const extracted = extractFromUrl(platform, candidate);
    if (extracted) return extracted;
    if (/^https?:\/\//i.test(s)) return s;
  }
  const cleaned = s.replace(/^@+/, '').split('?')[0].split('#')[0];
  if (platform === 'linkedin' && cleaned.startsWith('company/')) return cleaned;
  return cleaned.split('/')[0];
}

/**
 * Public profile link for Team pages (handles usernames or pasted URLs).
 */
export function teamSocialHref(platform, raw) {
  const s = trim(raw);
  if (!s) return '';
  const stored = normalizeTeamSocialHandle(platform, s);
  if (/^https?:\/\//i.test(stored)) {
    try {
      return new URL(stored).href;
    } catch {
      return '';
    }
  }
  return buildCanonicalUrl(platform, stored);
}
